import { Dialog } from '@angular/cdk/dialog'
import { CommonModule } from '@angular/common'
import {
  Component,
  EventEmitter,
  HostListener,
  Injector,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatPaginatorModule } from '@angular/material/paginator'
import { MatSelectModule } from '@angular/material/select'
import { MatTableModule } from '@angular/material/table'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { ProductService } from '../../services/product.service'
import { RouteService } from '../../services/route.service'
import { SpinnerService } from '../../services/spinner.service'
import { TranslationService } from '../../services/translation.service'
import { ProductFilter, ProductFilterInput } from '../../types/products'

@Component({
  selector: 'app-product-filter',
  imports: [
    CommonModule,
    MatInputModule,
    MatFormField,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatIcon,
  ],
  templateUrl: './product-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-filter.component.scss',
})
export class ProductFilterComponent implements OnInit {
  private injector = inject(Injector)
  dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private routeService = inject(RouteService)
  private productService = inject(ProductService)
  translationService = inject(TranslationService)

  @Output() filterOutput = new EventEmitter<ProductFilter>()

  filters: ProductFilterInput[] = [
    { label: 'Name', type: 'text' },
    { label: 'Brand', type: 'text' },
    { label: 'SKU', type: 'text' },
    { label: 'Batch', type: 'number' },
    { label: 'Serial', type: 'text' },
    { label: 'Gs1 Identifier', type: 'text' },
  ]

  filterValues: ProductFilter = {
    name: null,
    brand: null,
    sku: null,
    batch: null,
    serial: null,
    gs1Identifier: null,
  }

  selectedFilters: ProductFilterInput[] = [this.filters[0], this.filters[1]]

  displayedColumns: string[] = ['name', 'brand', 'viewAction']
  isMobileView = false

  async ngOnInit(): Promise<void> {
    this.checkWindowWidth()
  }

  /**
   * Checks the width of the window
   */
  checkWindowWidth() {
    const windowWidth = window.innerWidth
    this.isMobileView = windowWidth <= 767
  }

  /**
   * Updates the `isMobileView` property used to enable mobile-specific css classes on window resize
   */
  @HostListener('window:resize')
  onWindowResize() {
    this.checkWindowWidth()
  }

  /**
   * Returns the corresponding ProductFilter property name to be used for two-way data binding
   * NOTE - for this functionality to work, it is extremely important for any new property in the FilterValues interface to be
   * the camel case version of a filter's label. Example -> Gs1 Identifier should be stored as gs1Identifier
   * @param {string} filterLabel The label of the filter input
   * @returns {keyof ProductFilter | undefined}
   */
  getFilterValueProp(filterLabel: string): keyof ProductFilter | undefined {
    const s = filterLabel
      .match(
        /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
      )
      ?.map(
        (x: string) => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase(),
      )
      .join('')
    const camelCaseProp = s && s.slice(0, 1).toLowerCase() + s.slice(1)

    if (camelCaseProp && camelCaseProp in this.filterValues) {
      return camelCaseProp as keyof ProductFilter
    }

    return undefined
  }

  /**
   * Resets the filter
   */
  resetFilter() {
    this.selectedFilters = [this.filters[0], this.filters[1]]
    this.filterValues = {
      name: null,
      brand: null,
      serial: null,
      batch: null,
      sku: null,
      gs1Identifier: null,
    }
  }

  isFormValid(): boolean {
    // Check all selected filters with values to ensure they meet minimum length
    for (const filter of this.selectedFilters) {
      const propName = this.getFilterValueProp(filter.label)
      if (propName && this.filterValues[propName]) {
        // If there's a value and it's less than 2 characters, the form is invalid
        if (
          typeof this.filterValues[propName] === 'string' &&
          (this.filterValues[propName] as string).length < 2
        ) {
          return false
        }
      }
    }
    return true
  }

  filterChange() {
    if (this.isFormValid()) {
      this.filterOutput.emit(this.filterValues)
    }
  }
}
